import { TimeInMS } from 'types/time';

export const ENERGY_PROFILES_10S = {
  FCU: {
    12.0: {
      cooling: { chiller: 0.001236, gas: 0.001528 },
      heating: { 'hot water': 0.000715, 'heating coils': 0.001861 },
    },
    25.0: {
      cooling: { chiller: 0.002503, gas: 0.003111 },
      heating: { 'hot water': 0.001418, 'heating coils': 0.003806 },
    },
    50.0: {
      cooling: { chiller: 0.005035, gas: 0.00625 },
      heating: { 'hot water': 0.002865, 'heating coils': 0.007639 },
    },
    75.0: {
      cooling: { chiller: 0.007483, gas: 0.009306 },
      heating: { 'hot water': 0.004227, 'heating coils': 0.011389 },
    },
    100.0: {
      cooling: { chiller: 0.009931, gas: 0.012361 },
      heating: { 'hot water': 0.00559, 'heating coils': 0.015139 },
    },
    150.0: {
      cooling: { chiller: 0.014826, gas: 0.018472 },
      heating: { 'hot water': 0.008316, 'heating coils': 0.022639 },
    },
    200.0: {
      cooling: { chiller: 0.019722, gas: 0.024583 },
      heating: { 'hot water': 0.011042, 'heating coils': 0.030139 },
    },
    250.0: {
      cooling: { chiller: 0.024757, gas: 0.030833 },
      heating: { 'hot water': 0.013906, 'heating coils': 0.037778 },
    },
  },
  AHU: {
    100.0: {
      cooling: { chiller: 0.011458, gas: 0.013889 },
      heating: { 'hot water': 0.007118, 'heating coils': 0.016667 },
    },
    150.0: {
      cooling: { chiller: 0.017188, gas: 0.020833 },
      heating: { 'hot water': 0.010677, 'heating coils': 0.025 },
    },
    250.0: {
      cooling: { chiller: 0.028646, gas: 0.034722 },
      heating: { 'hot water': 0.017795, 'heating coils': 0.041667 },
    },
    300.0: {
      cooling: { chiller: 0.034375, gas: 0.041667 },
      heating: { 'hot water': 0.021354, 'heating coils': 0.05 },
    },
    350.0: {
      cooling: { chiller: 0.040104, gas: 0.048611 },
      heating: { 'hot water': 0.024913, 'heating coils': 0.058333 },
    },
    400.0: {
      cooling: { chiller: 0.045833, gas: 0.055556 },
      heating: { 'hot water': 0.028472, 'heating coils': 0.066667 },
    },
    450.0: {
      cooling: { chiller: 0.051563, gas: 0.0625 },
      heating: { 'hot water': 0.032031, 'heating coils': 0.075 },
    },
    500.0: {
      cooling: { chiller: 0.057292, gas: 0.069444 },
      heating: { 'hot water': 0.03559, 'heating coils': 0.083333 },
    },
  },
};

const ENERGY_TYPE_ALIASES = {
  'chiller (water)': 'chiller',
  'dx/vrf': 'gas',
  'dx / vrf': 'gas',
  'electric spiral': 'heating coils',
  'heat pump': 'hot water',
  'heating coil': 'heating coils',
};

export const parseNullableNumber = value => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeEnergyType = value =>
  ENERGY_TYPE_ALIASES[
    String(value || '')
      .trim()
      .toLowerCase()
  ] ??
  String(value || '')
    .trim()
    .toLowerCase();

const nearestProfileArea = (unitType, areaM2) => {
  const profiles = ENERGY_PROFILES_10S[unitType];
  if (!profiles) return null;
  const areas = Object.keys(profiles).map(Number).filter(Number.isFinite);
  if (areas.length === 0) return null;
  if (!Number.isFinite(areaM2)) return Math.min(...areas);
  return areas.reduce(
    (nearest, current) => (Math.abs(current - areaM2) < Math.abs(nearest - areaM2) ? current : nearest),
    areas[0],
  );
};

const effectiveAreaForProfile = (unitType, areaM2, areaCfm, areaFanKw) => {
  if (Number.isFinite(areaM2)) return areaM2;
  if (unitType === 'FCU' && Number.isFinite(areaCfm)) {
    const cfmToArea = {
      350.0: 12.0,
      600.0: 25.0,
      1000.0: 50.0,
      1500.0: 75.0,
      2000.0: 100.0,
      3000.0: 150.0,
      4000.0: 200.0,
      5000.0: 250.0,
    };
    const nearestCfm = Object.keys(cfmToArea)
      .map(Number)
      .reduce(
        (nearest, current) => (Math.abs(current - areaCfm) < Math.abs(nearest - areaCfm) ? current : nearest),
        350.0,
      );
    return cfmToArea[nearestCfm];
  }
  if (unitType === 'AHU' && Number.isFinite(areaFanKw)) {
    const fanKwToArea = {
      1.0: 100.0,
      1.5: 150.0,
      2.5: 250.0,
      3.0: 300.0,
      3.5: 350.0,
      4.0: 400.0,
      4.5: 450.0,
      5.0: 500.0,
    };
    const nearestKw = Object.keys(fanKwToArea)
      .map(Number)
      .reduce(
        (nearest, current) => (Math.abs(current - areaFanKw) < Math.abs(nearest - areaFanKw) ? current : nearest),
        1.0,
      );
    return fanKwToArea[nearestKw];
  }
  return null;
};

export const resolveTableKwh10s = (unitType, coolingType, heatingType, modeValue, areaM2, areaCfm, areaFanKw) => {
  const normalizedUnitType = String(unitType || '')
    .trim()
    .toUpperCase();
  const effectiveArea = effectiveAreaForProfile(normalizedUnitType, areaM2, areaCfm, areaFanKw);
  const profileArea = nearestProfileArea(normalizedUnitType, effectiveArea);
  if (!Number.isFinite(profileArea)) return null;

  const profile = ENERGY_PROFILES_10S[normalizedUnitType]?.[profileArea] ?? {};
  const isHeatingMode = String(modeValue || '').trim().toLowerCase() === 'heat';
  const energyBucket = isHeatingMode ? 'heating' : 'cooling';
  const energyType = normalizeEnergyType(isHeatingMode ? heatingType : coolingType);
  let tableKwh = profile?.[energyBucket]?.[energyType];

  if (!Number.isFinite(tableKwh)) {
    const fallbackType = isHeatingMode ? 'hot water' : 'chiller';
    tableKwh = profile?.[energyBucket]?.[fallbackType];
  }

  return Number.isFinite(tableKwh) ? tableKwh : null;
};

export const calculateKwhTotalWaste = ({
  unitType,
  coolingType,
  heatingType,
  modeValue = 'cool',
  areaM2,
  areaCfm,
  areaFanKw,
  totalCavHours,
}) => {
  const tableKwh10s = resolveTableKwh10s(unitType, coolingType, heatingType, modeValue, areaM2, areaCfm, areaFanKw);
  const totalCavTicks = Number.isFinite(totalCavHours) ? totalCavHours * 60 * 6 : null;
  return Number.isFinite(tableKwh10s) && Number.isFinite(totalCavTicks)
    ? Number((tableKwh10s * totalCavTicks).toFixed(2))
    : null;
};

const parseTimeToMinutes = value => {
  if (!value) return null;
  const parts = String(value).split(':').map(Number);
  if (parts.length < 2 || parts.some(Number.isNaN)) return null;
  const [hours, minutes = 0, seconds = 0] = parts;
  return hours * 60 + minutes + seconds / 60;
};

const resolveCavTimeForDay = (value, dayIndex) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    const dayValue = value[String(dayIndex)] ?? value[dayIndex];
    return dayValue ? String(dayValue).trim() : '';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '';
  }
  return '';
};

export const getCavHoursForSelectedTimeRange = (timeRange, cavStartTime, cavEndTime) => {
  const MS_PER_MINUTE = 60 * 1000;
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const getDayStart = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const hasExplicitTime = value => typeof value === 'string' && /[T:]/.test(value);
  let rangeStart;
  let rangeEnd;

  if (typeof timeRange === 'number') {
    rangeEnd = new Date();
    if (timeRange === TimeInMS.Hour) {
      rangeStart = new Date(rangeEnd.getTime() - timeRange);
    } else if (timeRange >= TimeInMS.Day) {
      const isWeek = timeRange === TimeInMS.Week;
      const startOffsetMs = isWeek ? timeRange : timeRange - ONE_DAY_MS;
      rangeStart = getDayStart(new Date(rangeEnd.getTime() - startOffsetMs));
    } else {
      rangeStart = new Date(rangeEnd.getTime() - timeRange);
    }
  } else if (typeof timeRange === 'string') {
    const [startStr, endStr] = timeRange.split('_');
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start) || isNaN(end)) throw new Error('Invalid date range format');
    rangeStart = hasExplicitTime(startStr) ? start : getDayStart(start);
    if (hasExplicitTime(endStr)) {
      rangeEnd = end;
    } else {
      const endDayStart = getDayStart(end);
      const todayStart = getDayStart(new Date());
      rangeEnd =
        endDayStart.getTime() === todayStart.getTime() ? new Date() : new Date(endDayStart.getTime() + ONE_DAY_MS - 1);
    }
  } else {
    throw new Error('Invalid time argument');
  }

  if (rangeEnd < rangeStart) return 0;
  let totalMinutes = 0;
  const firstDay = getDayStart(rangeStart);
  const lastDay = getDayStart(rangeEnd);
  for (let day = new Date(firstDay.getTime()); day <= lastDay; day = new Date(day.getTime() + ONE_DAY_MS)) {
    const dayStart = day;
    const dayEnd = new Date(dayStart.getTime() + ONE_DAY_MS);
    const dayIndex = dayStart.getDay();
    const startMinutes = parseTimeToMinutes(resolveCavTimeForDay(cavStartTime, dayIndex));
    const endMinutes = parseTimeToMinutes(resolveCavTimeForDay(cavEndTime, dayIndex));
    if (startMinutes === null || endMinutes === null) continue;
    const isAllDayActive = startMinutes === 0 && endMinutes === 0;
    if (!isAllDayActive && startMinutes === endMinutes) continue;
    const intervals = [];
    if (isAllDayActive) {
      intervals.push([dayStart, dayEnd]);
    } else if (startMinutes < endMinutes) {
      intervals.push([
        new Date(dayStart.getTime() + startMinutes * MS_PER_MINUTE),
        new Date(dayStart.getTime() + endMinutes * MS_PER_MINUTE),
      ]);
    } else {
      intervals.push([new Date(dayStart.getTime() + startMinutes * MS_PER_MINUTE), dayEnd]);
      intervals.push([dayStart, new Date(dayStart.getTime() + endMinutes * MS_PER_MINUTE)]);
    }
    for (const [intervalStart, intervalEnd] of intervals) {
      const overlapStart = intervalStart > rangeStart ? intervalStart : rangeStart;
      const overlapEnd = intervalEnd < rangeEnd ? intervalEnd : rangeEnd;
      const overlapMs = overlapEnd - overlapStart;
      if (overlapMs > 0) totalMinutes += overlapMs / MS_PER_MINUTE;
    }
  }
  return Math.round((totalMinutes / 60) * 10) / 10;
};

const resolveActiveWindowForDay = (activeHoursByDay, dayIndex) => {
  if (!activeHoursByDay || typeof activeHoursByDay !== 'object') return null;

  const directEntry = activeHoursByDay[String(dayIndex)] ?? activeHoursByDay[dayIndex];
  const startTimeByDay = activeHoursByDay.startTimeByDay;
  const endTimeByDay = activeHoursByDay.endTimeByDay;
  const isActiveByDay = activeHoursByDay.isActiveByDay;

  const readDayValue = (value, key) => {
    if (value === null || value === undefined) return undefined;
    if (typeof value !== 'object' || Array.isArray(value)) return value;
    return value[String(key)] ?? value[key];
  };

  let isActive;
  let startValue;
  let endValue;

  if (directEntry && typeof directEntry === 'object' && !Array.isArray(directEntry)) {
    isActive = directEntry.isActive;
    startValue = directEntry.startTime ?? directEntry.start;
    endValue = directEntry.endTime ?? directEntry.end;
  }

  if (startValue === undefined) startValue = readDayValue(startTimeByDay, dayIndex);
  if (endValue === undefined) endValue = readDayValue(endTimeByDay, dayIndex);
  if (isActive === undefined) isActive = readDayValue(isActiveByDay, dayIndex);

  if (isActive === false) return null;

  const startMinutes = parseTimeToMinutes(startValue);
  const endMinutes = parseTimeToMinutes(endValue);
  if (startMinutes === null || endMinutes === null) return null;

  return { startMinutes, endMinutes };
};

export const getExpectedRecordsForSelectedTimeRange = (timeRange, activeHoursByDay = null) => {
  const FIVE_MINUTES_MS = 5 * 60 * 1000;
  const MS_PER_MINUTE = 60 * 1000;
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const getDayStart = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const hasExplicitTime = value => typeof value === 'string' && /[T:]/.test(value);
  const parseDateValue = value => {
    if (value instanceof Date) return isNaN(value) ? null : value;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const dottedDateMatch = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
    if (dottedDateMatch) {
      const [, dayStr, monthStr, yearStr] = dottedDateMatch;
      const day = Number(dayStr);
      const month = Number(monthStr) - 1;
      const year = Number(yearStr);
      const parsed = new Date(year, month, day);
      if (parsed.getFullYear() !== year || parsed.getMonth() !== month || parsed.getDate() !== day) {
        return null;
      }
      return parsed;
    }
    const parsed = new Date(trimmed);
    return isNaN(parsed) ? null : parsed;
  };
  let rangeStart;
  let rangeEnd;

  if (typeof timeRange === 'number') {
    rangeEnd = new Date();
    if (timeRange === TimeInMS.Hour) {
      rangeStart = new Date(rangeEnd.getTime() - timeRange);
    } else if (timeRange >= TimeInMS.Day) {
      const isWeek = timeRange === TimeInMS.Week;
      const startOffsetMs = isWeek ? timeRange : timeRange - ONE_DAY_MS;
      rangeStart = getDayStart(new Date(rangeEnd.getTime() - startOffsetMs));
    } else {
      rangeStart = new Date(rangeEnd.getTime() - timeRange);
    }
  } else if (typeof timeRange === 'string') {
    const [startStr, endStr] = timeRange.split('_');
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start) || isNaN(end)) return 0;
    rangeStart = hasExplicitTime(startStr) ? start : getDayStart(start);
    if (hasExplicitTime(endStr)) {
      rangeEnd = end;
    } else {
      const endDayStart = getDayStart(end);
      const todayStart = getDayStart(new Date());
      rangeEnd =
        endDayStart.getTime() === todayStart.getTime() ? new Date() : new Date(endDayStart.getTime() + ONE_DAY_MS - 1);
    }
  } else if (timeRange && typeof timeRange === 'object') {
    const startValue = timeRange.startDate ?? timeRange.start;
    const endValue = timeRange.endDate ?? timeRange.end;
    const start = parseDateValue(startValue);
    const end = parseDateValue(endValue);
    if (!start || !end) return 0;
    rangeStart = hasExplicitTime(startValue) ? start : getDayStart(start);
    if (hasExplicitTime(endValue)) {
      rangeEnd = end;
    } else {
      const endDayStart = getDayStart(end);
      const todayStart = getDayStart(new Date());
      rangeEnd =
        endDayStart.getTime() === todayStart.getTime() ? new Date() : new Date(endDayStart.getTime() + ONE_DAY_MS - 1);
    }
  } else {
    return 0;
  }

  const windowMs = rangeEnd - rangeStart;
  if (!Number.isFinite(windowMs) || windowMs <= 0) return 0;

  if (!activeHoursByDay || typeof activeHoursByDay !== 'object') {
    return Math.ceil(windowMs / FIVE_MINUTES_MS);
  }

  let activeOverlapMs = 0;
  const firstDay = getDayStart(rangeStart);
  const lastDay = getDayStart(rangeEnd);

  for (let day = new Date(firstDay.getTime()); day <= lastDay; day = new Date(day.getTime() + ONE_DAY_MS)) {
    const dayStart = day;
    const dayEnd = new Date(dayStart.getTime() + ONE_DAY_MS);
    const dayIndex = dayStart.getDay();
    const schedule = resolveActiveWindowForDay(activeHoursByDay, dayIndex);
    if (!schedule) continue;

    const { startMinutes, endMinutes } = schedule;
    const isAllDayActive = startMinutes === 0 && endMinutes === 0;
    if (!isAllDayActive && startMinutes === endMinutes) continue;

    const intervals = [];
    if (isAllDayActive) {
      intervals.push([dayStart, dayEnd]);
    } else if (startMinutes < endMinutes) {
      intervals.push([
        new Date(dayStart.getTime() + startMinutes * MS_PER_MINUTE),
        new Date(dayStart.getTime() + endMinutes * MS_PER_MINUTE),
      ]);
    } else {
      intervals.push([new Date(dayStart.getTime() + startMinutes * MS_PER_MINUTE), dayEnd]);
      intervals.push([dayStart, new Date(dayStart.getTime() + endMinutes * MS_PER_MINUTE)]);
    }

    for (const [intervalStart, intervalEnd] of intervals) {
      const overlapStart = intervalStart > rangeStart ? intervalStart : rangeStart;
      const overlapEnd = intervalEnd < rangeEnd ? intervalEnd : rangeEnd;
      const overlapMs = overlapEnd - overlapStart;
      if (overlapMs > 0) activeOverlapMs += overlapMs;
    }
  }

  if (activeOverlapMs <= 0) return 0;
  return Math.ceil(activeOverlapMs / FIVE_MINUTES_MS);
};
